
import { Message } from '../hooks/useChatMessages';
import { DynamicRoadmapConfig } from '../services/dynamicRoadmapGenerator';
import { Language, detectLanguage } from './languageUtils';

/**
 * Checks if a roadmap module should fallback to a template based on title/description language.
 * Returns the fallback text for various keys.
 */
export const getRoadmapFallbackText = (key: string, titlePlusDesc: string): string => {
    const lang = detectLanguage(titlePlusDesc);

    if (lang === 'zh-CN') {
        const chineseTexts: Record<string, string> = {
            title: '🌟 分享探索',
            description: '周计划：帮助您的孩子练习分享玩具！',
            improveSkills: '提高技能',
            buildConfidence: '建立信心',
            beConsistent: '保持一致性',
            celebrateProgress: '庆祝进步'
        };
        return chineseTexts[key] || key;
    } else if (lang === 'es') {
        const spanishTexts: Record<string, string> = {
            title: '🌟 Exploración de Compartir',
            description: '¡Plan semanal: Ayuda a tu hijo a practicar compartir juguetes!',
            improveSkills: 'Mejorar habilidades',
            buildConfidence: 'Construir confianza',
            beConsistent: 'Ser consistente',
            celebrateProgress: 'Celebrar progreso'
        };
        return spanishTexts[key] || key;
    } else if (lang === 'fr') {
        const frenchTexts: Record<string, string> = {
            title: '🌟 Exploration du Partage',
            description: 'Plan hebdomadaire : Aidez votre enfant à pratiquer le partage de jouets !',
            improveSkills: 'Améliorer les compétences',
            buildConfidence: 'Construire la confiance',
            beConsistent: 'Être cohérent',
            celebrateProgress: 'Célébrer le progrès'
        };
        return frenchTexts[key] || key;
    } else {
        const englishTexts: Record<string, string> = {
            title: '🌟 Share Quest',
            description: 'Week plan: Help your child practice sharing toys!',
            improveSkills: 'Improve skills',
            buildConfidence: 'Build confidence',
            beConsistent: 'Be consistent',
            celebrateProgress: 'Celebrate progress'
        };
        return englishTexts[key] || key;
    }
};

/**
 * Translates a dynamic roadmap to the target language (currently only supports Chinese translation).
 */
export const translateRoadmap = (roadmap: DynamicRoadmapConfig, targetLanguage: Language): DynamicRoadmapConfig => {
    // Clone to avoid mutating original
    const newRoadmap = JSON.parse(JSON.stringify(roadmap));

    if (targetLanguage === 'zh-CN') {
        // Translate the roadmap content to Chinese
        newRoadmap.title = newRoadmap.title.replace(/Training Adventure/i, '训练冒险之旅');
        newRoadmap.title = newRoadmap.title.replace(/Roadmap/i, '路线图');
        newRoadmap.title = newRoadmap.title.replace(/Day/i, '天');

        newRoadmap.description = newRoadmap.description.replace(/A \d+-day plan to help with your question/i, '一个帮助您解决宠物问题的个性化训练计划');

        // Translate goals
        newRoadmap.goals = newRoadmap.goals.map((goal: string) => {
            if (goal === 'Improve obedience') return '提高服从性';
            if (goal === 'Build trust') return '建立信任';
            if (goal === 'Enhance communication') return '增强沟通';
            if (goal === 'Develop skills') return '发展技能';
            return goal;
        });

        // Translate tips
        newRoadmap.tips = newRoadmap.tips.map((tip: string) => {
            if (tip === 'Be consistent') return '保持一致性';
            if (tip === 'Use positive reinforcement') return '使用积极强化';
            if (tip === 'Keep sessions short') return '保持训练时间短促';
            if (tip === 'Celebrate progress') return '庆祝进步';
            return tip;
        });

        // Translate daily activities
        newRoadmap.dailyActivities = newRoadmap.dailyActivities.map((day: any) => ({
            ...day,
            title: day.title
                .replace(/Day \d+:\s*Sit for Success/i, '第1天：坐下小能手')
                .replace(/Day \d+:\s*Walk Well/i, '第2天：散步小专家')
                .replace(/Day \d+:\s*Stay Strong/i, '第3天：停留小冠军')
                .replace(/Day \d+:\s*Come Command/i, '第4天：过来小英雄')
                .replace(/Day \d+:\s*Trust Builder/i, '第5天：信任小建设者')
                .replace(/Day \d+:\s*Communication Master/i, '第6天：沟通小大师')
                .replace(/Day \d+:\s*Skill Combiner/i, '第7天：技能小整合者'),
            description: day.description
                .replace(/Practice sit command consistently/i, '练习坐下命令的一致性')
                .replace(/Master loose leash walking/i, '掌握松 leash 散步')
                .replace(/Learn stay command with distractions/i, '学习在干扰下保持不动')
                .replace(/Perfect the recall command/i, '完善召回命令')
                .replace(/Build strong owner-pet bond/i, '建立牢固的主人-宠物纽带')
                .replace(/Combine multiple commands/i, '结合多个命令')
                .replace(/Show off all learned skills/i, '展示所有学到的技能'),
            activities: day.activities.map((activity: string) => {
                if (activity.includes('practice sit command')) return '练习坐下命令';
                if (activity.includes('loose leash walking')) return '练习松 leash 散步';
                if (activity.includes('stay with distractions')) return '练习在干扰下保持不动';
                if (activity.includes('recall command')) return '练习召回命令';
                if (activity.includes('positive reinforcement')) return '使用积极强化';
                if (activity.includes('short training sessions')) return '进行短时间的训练课程';
                if (activity.includes('combine sit and stay')) return '结合坐下和停留命令';
                if (activity.includes('introduce new commands')) return '介绍新命令';
                if (activity.includes('practice in different environments')) return '在不同环境中练习';
                if (activity.includes('increase duration')) return '增加保持时间';
                if (activity.includes('add verbal cues')) return '添加口头提示';
                if (activity.includes('gradual distance increase')) return '逐渐增加距离';
                if (activity.includes('emergency recall')) return '练习紧急召回';
                if (activity.includes('off-leash training')) return '练习无 leash 训练';
                if (activity.includes('group training')) return '进行小组训练';
                if (activity.includes('advanced commands')) return '学习高级命令';
                if (activity.includes('behavior modification')) return '行为修正训练';
                if (activity.includes('confidence building')) return '建立信心训练';
                if (activity.includes('socialization skills')) return '社交技能训练';
                return activity;
            })
        }));
    }

    return newRoadmap;
};

/**
 * Checks for missed Plan Q&A context in messages and returns the context if found.
 */
export const extractPlanContextFromMessages = (
    messages: Message[],
    hasPlanQaMessages: boolean,
    currentPlanContext: any | null
) => {
    // Check if any message has planContext (which indicates it's a Plan Q&A message)
    const planQaMessages = messages.filter(message => message.planContext !== undefined);
    const hasPlanQa = planQaMessages.length > 0;

    // If we have Plan Q&A messages but no plan context, restore it from the latest day message
    if (hasPlanQa && !currentPlanContext) {
        // Find the message with the highest day number (latest day)
        const latestPlanQaMessage = planQaMessages.reduce((latest, current) => {
            if (!latest.planContext || !current.planContext) return latest;
            return current.planContext.day > latest.planContext.day ? current : latest;
        });

        if (latestPlanQaMessage.planContext) {
            return {
                planId: latestPlanQaMessage.planContext.planId,
                day: latestPlanQaMessage.planContext.day,
                contextType: latestPlanQaMessage.planContext.contextType,
                contextElement: latestPlanQaMessage.planContext.contextElement,
                planTitle: `Day ${latestPlanQaMessage.planContext.day}`,
                planDescription: ''
            };
        }
    }
    return null;
};

/**
 * Generates the localized suggestion text for the roadmap.
 */
export const getRoadmapSuggestionText = (duration: string | number, language: Language = 'en'): string => {
    if (language === 'zh-CN') {
        return `我为您创建了一个个性化的${duration}天宠物训练计划！这个路线图是根据您宠物的具体情况量身定制的，将帮助您的宠物稳步进步。`;
    } else if (language === 'es') {
        return `¡He creado un plan de entrenamiento personalizado de ${duration} días para tu mascota! Esta hoja de ruta está adaptada a la situación específica de tu mascota y te ayudará a progresar constantemente.`;
    } else if (language === 'fr') {
        return `J'ai créé un plan d'entraînement personnalisé de ${duration} jours pour votre animal ! Cette feuille de route est adaptée à la situation spécifique de votre animal et vous aidera à progresser régulièrement.`;
    } else {
        return `I've created a personalized ${duration}-day pet training plan for you! This roadmap is tailored to your pet's specific situation and will help your pet make steady progress.`;
    }
};

/**
 * Generate personalized prompts for each conversational step.
 */
export const generateConversationalStepPrompt = (
    stepType: string,
    originalQuestion: string,
    conversationContext: string,
    _stepIndex: number
): string => {
    const lang = detectLanguage(originalQuestion);
    const language = lang === 'zh-CN' ? 'Chinese' : lang === 'es' ? 'Spanish' : lang === 'fr' ? 'French' : 'English';

    const baseContext = `You are a home visiting assistant helping a parent with their child-related concern. The parent originally asked: "${originalQuestion}". \n\nConversation so far:\n${conversationContext}\n\nIMPORTANT: Respond directly without any prefix like "Home Visitor:" or "Assistant:". Start your response immediately with your message.\n\nRespond ONLY in ${language}. Be warm, understanding, and professional.`;

    switch (stepType) {
        case 'listen':
            return `${baseContext}\n\nThis is Step 1: LISTEN. Acknowledge their concern and show empathy. Keep it brief and warm.`;
        case 'clarify':
            return `${baseContext}\n\nThis is Step 2: CLARIFY. Ask 2-3 specific questions about frequency, what they've tried, and context.`;
        case 'summarize':
            return `${baseContext}\n\nThis is Step 3: SUMMARIZE. Reflect back what you've heard in your own words.`;
        case 'immediate_guidance':
            return `${baseContext}\n\nThis is Step 4: IMMEDIATE GUIDANCE. Provide 2-3 practical, actionable tips they can try right now.`;
        case 'ask_permission':
            return `${baseContext}\n\nThis is Step 5: ASK PERMISSION. Suggest a structured learning plan and ask for permission to create a personalized roadmap.`;
        case 'continue_dialogue':
            return `${baseContext}\n\nThe parent declined the roadmap. Continue providing support through conversation.`;
        default:
            return `${baseContext}\n\nContinue the supportive conversation appropriately for this step.`;
    }
};
